// Timeline of the North OS Meetings demo, kept free of React and of the DOM.
//   - STEPS: what happens, in order, over one loop (ms).
//   - stateAt(t): what the screen shows at time t (tested).
//   - STILL_STATE: the frame shown without motion (reduced motion, no JS): it is also the page's static markup.
//   - buildTracks(): Web Animations keyframes for every animated element, anchored on the same STEPS times.

import { TIMER_STEPS, TLDR, ACTION_ITEMS } from "./meeting-data";

export const LOOP_MS = 16600;

export const STEPS = [
  { id: "poster", at: 0 },
  { id: "cursor-in", at: 600 },
  { id: "record-hover", at: 1600 },
  { id: "record-press", at: 1900 },
  { id: "recording", at: 2150 },
  { id: "stop-hover", at: 3850 },
  { id: "stop-press", at: 4150 },
  { id: "prompt-out", at: 4400 },
  { id: "meeting-in", at: 4750 },
  { id: "ready", at: 7100 },
  { id: "row-hover", at: 8200 },
  { id: "row-press", at: 8600 },
  { id: "panel-open", at: 8800 },
  { id: "tldr", at: 9500 },
  { id: "actions", at: 11600 },
  { id: "check", at: 13300 },
  { id: "panel-close", at: 14700 },
  { id: "fade-out", at: 15300 },
  { id: "fade-in", at: 15900 },
] as const;

export type StepId = (typeof STEPS)[number]["id"];

const at = (id: StepId) => STEPS.find((step) => step.id === id)!.at;

// Sub-beats inside a step.
const TLDR_GAP = 700; // one sentence every 700 ms
const ACTION_GAP = 200; // action items arrive 200 ms apart
const CURSOR_OUT = 14400; // cursor gone before the panel closes

// The detail panel opens in 600 ms and closes faster. It arrives turned a few degrees in its own perspective and
// settles flat; the reverse on the way out. Reduced motion or no JS: none of this runs, the still frame is flat.
const PANEL_IN = 600;
const PANEL_OUT = 380;
const PANEL_AWAY = "translateX(calc(100% + 24px))";
const PANEL_HOME = "translateX(0px)";
const TURN_AWAY = "perspective(1200px) rotateY(-11deg) rotateX(3deg) scale(0.965)";
const TURN_FLAT = "perspective(1200px) rotateY(0deg) rotateX(0deg) scale(1)";

export type DemoState = {
  step: StepId;
  cursor: boolean;
  prompt: "idle" | "hover" | "pressed" | "recording" | "gone";
  meeting: "absent" | "transcribing" | "ready";
  rowHover: boolean;
  panel: "closed" | "open";
  tldr: number;
  actions: number;
  checked: boolean;
};

const within = (t: number, from: number, to: number) => t >= from && t < to;

export function stateAt(time: number): DemoState {
  const t = ((time % LOOP_MS) + LOOP_MS) % LOOP_MS;
  const step = [...STEPS].reverse().find((s) => s.at <= t)!.id;
  const reset = at("fade-in"); // everything below returns to the poster while the list is faded out

  let prompt: DemoState["prompt"] = "idle";
  if (within(t, at("record-hover"), at("record-press"))) prompt = "hover";
  else if (within(t, at("record-press"), at("recording"))) prompt = "pressed";
  else if (within(t, at("recording"), at("prompt-out"))) prompt = "recording";
  else if (within(t, at("prompt-out"), reset)) prompt = "gone";

  let meeting: DemoState["meeting"] = "absent";
  if (within(t, at("meeting-in"), at("ready"))) meeting = "transcribing";
  else if (within(t, at("ready"), reset)) meeting = "ready";

  const count = (start: number, gap: number, max: number) =>
    t >= reset ? 0 : Math.max(0, Math.min(max, Math.floor((t - start) / gap) + 1));

  return {
    step,
    cursor: within(t, at("cursor-in"), CURSOR_OUT),
    prompt,
    meeting,
    rowHover: within(t, at("row-hover"), at("panel-open") + 600),
    panel: within(t, at("panel-open"), at("panel-close")) ? "open" : "closed",
    tldr: count(at("tldr"), TLDR_GAP, TLDR.length),
    actions: count(at("actions"), ACTION_GAP, ACTION_ITEMS.length),
    checked: within(t, at("check") + 100, reset),
  };
}

/** Frame shown without motion: the new meeting is ready, the panel open, the first action checked. */
export const STILL_AT = at("check") + 800;
export const STILL_STATE: DemoState = { ...stateAt(STILL_AT), cursor: false };

// ---------- Web Animations tracks ----------

export type Point = { x: number; y: number };
export type Targets = { record: Point; stop: Point; row: Point; check: Point };
export type Track = { key: string; keyframes: Keyframe[]; duration: number };

type Style = Record<string, string | number>;
type Beat = [ms: number, style: Style, easing?: string];

export const EASE = {
  out: "cubic-bezier(0.22, 1, 0.36, 1)",
  inOut: "cubic-bezier(0.65, 0, 0.35, 1)",
  in: "cubic-bezier(0.5, 0, 0.75, 0)",
  pop: "cubic-bezier(0.34, 1.56, 0.64, 1)",
  step: "steps(1, end)",
};

/** Keyframes over `duration`. A beat's easing applies from that beat to the next one; the first style is held
 *  back to 0 and the last one forward to the end, so a track whose first and last styles match loops seamlessly. */
function track(key: string, beats: Beat[], duration = LOOP_MS): Track {
  const keyframes: Keyframe[] = beats.map(([ms, style, easing]) => ({
    offset: ms / duration,
    ...style,
    ...(easing ? { easing } : {}),
  }));
  const first = beats[0]!;
  const last = beats[beats.length - 1]!;
  if (first[0] > 0) keyframes.unshift({ offset: 0, ...first[1] });
  if (last[0] < duration) keyframes.push({ offset: 1, ...last[1] });
  return { key, keyframes, duration };
}

const px = (p: Point, scale = 1) => ({ transform: `translate(${p.x.toFixed(1)}px, ${p.y.toFixed(1)}px) scale(${scale})` });
const shown = (on: boolean) => ({ opacity: on ? 1 : 0 });

/** Two beats at the same time: an instant jump, used while the element cannot be seen. */
const jump = (ms: number, from: Style, to: Style, easing?: string): Beat[] => [
  [ms, from],
  [ms, to, easing],
];

/**
 * `shift` is how far the list moves up when the new meeting is taken out of it — the new row's height plus the
 * gap, measured in the page. Every past row travels THAT distance, not its own height: below 720 the rows no
 * longer have the same height (titles wrap, and only the new meeting keeps a summary), so `-100%` would move
 * each row by a different amount and open a hole where the new meeting is about to land. Left out, the old
 * per-row `-100%` is used, which is the same number at every width where the rows are equal.
 */
export function buildTracks(targets: Targets, stage: { width: number; height: number }, shift?: number): Track[] {
  const clamp = (p: Point): Point => ({
    x: Math.min(stage.width - 24, Math.max(8, p.x)),
    y: Math.min(stage.height - 24, Math.max(8, p.y)),
  });
  const start = clamp({ x: targets.record.x - 150, y: targets.record.y - 110 });
  const rest = clamp({ x: (targets.stop.x + targets.row.x) / 2, y: targets.row.y + 140 });
  const exit = clamp({ x: targets.check.x + 90, y: targets.check.y + 60 });
  const cursor = (p: Point, scale = 1, visible = true) => ({ ...px(p, scale), ...shown(visible) });
  const reset = at("fade-in");
  const up = `translateY(${shift != null ? `${-shift}px` : "calc(-100% - 10px)"})`;

  const tracks: Track[] = [
    track("cursor", [
      [0, cursor(start, 1, false)],
      [at("cursor-in"), cursor(start, 1, false), EASE.out],
      [at("cursor-in") + 250, cursor(start), EASE.inOut],
      [at("record-hover") - 50, cursor(targets.record)],
      [at("record-press"), cursor(targets.record), EASE.out],
      [at("record-press") + 90, cursor(targets.record, 0.88), EASE.out],
      [at("record-press") + 220, cursor(targets.record)],
      [at("recording") + 150, cursor(targets.record), EASE.inOut],
      [at("stop-hover") - 100, cursor(targets.stop)],
      [at("stop-press"), cursor(targets.stop), EASE.out],
      [at("stop-press") + 90, cursor(targets.stop, 0.88), EASE.out],
      [at("stop-press") + 220, cursor(targets.stop)],
      [at("prompt-out") + 100, cursor(targets.stop), EASE.inOut],
      [at("meeting-in") + 900, cursor(rest)],
      [at("ready") + 400, cursor(rest), EASE.inOut],
      [at("row-hover") - 50, cursor(targets.row)],
      [at("row-press"), cursor(targets.row), EASE.out],
      [at("row-press") + 90, cursor(targets.row, 0.88), EASE.out],
      [at("row-press") + 220, cursor(targets.row)],
      [at("actions") + 700, cursor(targets.row), EASE.inOut],
      [at("check") - 150, cursor(targets.check)],
      [at("check"), cursor(targets.check), EASE.out],
      [at("check") + 90, cursor(targets.check, 0.88), EASE.out],
      [at("check") + 220, cursor(targets.check)],
      [CURSOR_OUT - 700, cursor(targets.check), EASE.inOut],
      [CURSOR_OUT, cursor(exit, 1, false)],
      ...jump(CURSOR_OUT + 50, cursor(exit, 1, false), cursor(start, 1, false)),
    ]),

    // Recording prompt: folds away once stopped, pops back for the next loop.
    track("prompt", [
      [0, { opacity: 1, transform: "scale(1)" }],
      [at("prompt-out"), { opacity: 1, transform: "scale(1)" }, EASE.in],
      [at("prompt-out") + 300, { opacity: 0, transform: "scale(0.94)" }],
      [reset, { opacity: 0, transform: "scale(0.94)" }, EASE.pop],
      [reset + 600, { opacity: 1, transform: "scale(1)" }],
    ]),
    track("prompt-idle", [
      [0, shown(true)],
      [at("recording"), shown(true), EASE.out],
      [at("recording") + 250, shown(false)],
      ...jump(reset - 300, shown(false), shown(true)),
    ]),
    track("prompt-rec", [
      [0, shown(false)],
      [at("recording") + 50, shown(false), EASE.out],
      [at("recording") + 350, shown(true)],
      ...jump(reset - 300, shown(true), shown(false)),
    ]),
    track("record-hover", [
      [0, shown(false)],
      [at("record-hover"), shown(false), EASE.out],
      [at("record-hover") + 180, shown(true)],
      [at("recording") + 150, shown(true)],
      [at("recording") + 300, shown(false)],
    ]),
    track("record-btn", [
      [0, { transform: "scale(1)" }],
      [at("record-press"), { transform: "scale(1)" }, EASE.out],
      [at("record-press") + 90, { transform: "scale(0.97)" }, EASE.out],
      [at("record-press") + 220, { transform: "scale(1)" }],
    ]),
    track("stop-hover", [
      [0, shown(false)],
      [at("stop-hover"), shown(false), EASE.out],
      [at("stop-hover") + 180, shown(true)],
      ...jump(reset - 300, shown(true), shown(false)),
    ]),
    track("stop-btn", [
      [0, { transform: "scale(1)" }],
      [at("stop-press"), { transform: "scale(1)" }, EASE.out],
      [at("stop-press") + 90, { transform: "scale(0.97)" }, EASE.out],
      [at("stop-press") + 220, { transform: "scale(1)" }],
    ]),
    track(
      "timer",
      TIMER_STEPS.map((_, i): Beat => {
        const ms = i < 3 ? at("recording") + i * 550 : at("recording") + 1100 + (i - 2) * 160;
        return [ms, { transform: `translateY(${(-100 * i) / TIMER_STEPS.length}%)` }, EASE.step];
      }).concat(jump(reset - 300, { transform: `translateY(${(-100 * (TIMER_STEPS.length - 1)) / TIMER_STEPS.length}%)` }, { transform: "translateY(0%)" })),
    ),

    // List: past meetings slide down to make room; the whole list fades out and back to close the loop.
    track("list", [
      [0, shown(true)],
      [at("fade-out"), shown(true), EASE.inOut],
      [at("fade-out") + 500, shown(false)],
      [reset, shown(false), EASE.out],
      [reset + 600, shown(true)],
    ]),
    track("row-past", [
      [0, { transform: up }],
      [at("meeting-in"), { transform: up }, EASE.out],
      [at("meeting-in") + 650, { transform: "translateY(0px)" }],
      ...jump(reset, { transform: "translateY(0px)" }, { transform: up }),
    ]),
    track("row-new", [
      [0, { opacity: 0, transform: "translateY(-12px) scale(0.98)" }],
      [at("meeting-in") + 100, { opacity: 0, transform: "translateY(-12px) scale(0.98)" }, EASE.out],
      [at("meeting-in") + 700, { opacity: 1, transform: "translateY(0px) scale(1)" }],
      [at("row-press"), { opacity: 1, transform: "translateY(0px) scale(1)" }, EASE.out],
      [at("row-press") + 90, { opacity: 1, transform: "translateY(0px) scale(0.99)" }, EASE.out],
      [at("row-press") + 220, { opacity: 1, transform: "translateY(0px) scale(1)" }],
      ...jump(reset, { opacity: 1, transform: "translateY(0px) scale(1)" }, { opacity: 0, transform: "translateY(-12px) scale(0.98)" }),
    ]),
    track("row-shimmer", [
      [0, { opacity: 0, transform: "translateX(-100%)" }],
      [at("meeting-in") + 600, { opacity: 1, transform: "translateX(-100%)" }, EASE.inOut],
      ...jump(at("meeting-in") + 1450, { opacity: 1, transform: "translateX(260%)" }, { opacity: 1, transform: "translateX(-100%)" }, EASE.inOut),
      [at("ready") - 50, { opacity: 1, transform: "translateX(260%)" }],
      [at("ready"), { opacity: 0, transform: "translateX(-100%)" }],
    ]),
    track("status-trans", [
      [0, shown(true)],
      [at("ready"), shown(true), EASE.out],
      [at("ready") + 200, shown(false)],
      ...jump(reset, shown(false), shown(true)),
    ]),
    track("status-ready", [
      [0, { opacity: 0, transform: "scale(0.85)" }],
      [at("ready") + 50, { opacity: 0, transform: "scale(0.85)" }, EASE.pop],
      [at("ready") + 400, { opacity: 1, transform: "scale(1)" }],
      ...jump(reset, { opacity: 1, transform: "scale(1)" }, { opacity: 0, transform: "scale(0.85)" }),
    ]),
    track("row-flash", [
      [0, shown(false)],
      [at("ready"), shown(false), EASE.out],
      [at("ready") + 150, shown(true), EASE.inOut],
      [at("ready") + 900, shown(false)],
    ]),
    track("row-skeleton", [
      [0, shown(true)],
      [at("ready"), shown(true), EASE.out],
      [at("ready") + 200, shown(false)],
      ...jump(reset, shown(false), shown(true)),
    ]),
    track("row-summary", [
      [0, { opacity: 0, transform: "translateY(4px)" }],
      [at("ready") + 150, { opacity: 0, transform: "translateY(4px)" }, EASE.out],
      [at("ready") + 550, { opacity: 1, transform: "translateY(0px)" }],
      ...jump(reset, { opacity: 1, transform: "translateY(0px)" }, { opacity: 0, transform: "translateY(4px)" }),
    ]),
    track("row-hover", [
      [0, shown(false)],
      [at("row-hover"), shown(false), EASE.out],
      [at("row-hover") + 180, shown(true)],
      [at("panel-open") + 500, shown(true), EASE.out],
      [at("panel-open") + 800, shown(false)],
    ]),
    track("row-chevron", [
      [0, { opacity: 0, transform: "translateX(-4px)" }],
      [at("row-hover"), { opacity: 0, transform: "translateX(-4px)" }, EASE.out],
      [at("row-hover") + 180, { opacity: 1, transform: "translateX(0px)" }],
      [at("panel-open") + 500, { opacity: 1, transform: "translateX(0px)" }, EASE.out],
      [at("panel-open") + 800, { opacity: 0, transform: "translateX(-4px)" }],
    ]),

    // Detail panel: slides in over the main column, writes its TL;DR, lists the actions, checks the first one.
    // The opening is in two carriers, so each keeps its own easing: the panel slides (ease-out, in place fast), the
    // wrapper turns (ease-in-out, so the last few degrees close after the slide is done and the card lands flat).
    track("panel", [
      [0, { transform: PANEL_AWAY }],
      [at("panel-open"), { transform: PANEL_AWAY }, EASE.out],
      [at("panel-open") + PANEL_IN, { transform: PANEL_HOME }],
      [at("panel-close") + 60, { transform: PANEL_HOME }, EASE.in],
      [at("panel-close") + PANEL_OUT, { transform: PANEL_AWAY }],
    ]),
    track("panel-turn", [
      [0, { transform: TURN_AWAY }],
      [at("panel-open"), { transform: TURN_AWAY }, EASE.inOut],
      [at("panel-open") + PANEL_IN, { transform: TURN_FLAT }],
      [at("panel-close"), { transform: TURN_FLAT }, EASE.out],
      [at("panel-close") + 240, { transform: TURN_AWAY }],
    ]),
    // The shadow of a card in flight: up as it leaves the edge, back to the resting shadow once it is flat.
    track("panel-shade", [
      [0, shown(false)],
      [at("panel-open"), shown(false), EASE.out],
      [at("panel-open") + 140, shown(true), EASE.inOut],
      [at("panel-open") + PANEL_IN, shown(false)],
      [at("panel-close"), shown(false), EASE.out],
      [at("panel-close") + 110, { opacity: 0.9 }, EASE.inOut],
      [at("panel-close") + PANEL_OUT, shown(false)],
    ]),
    ...TLDR.map((_, i) => {
      const from = at("tldr") + i * TLDR_GAP;
      const hidden = { opacity: 0, filter: "blur(4px)" };
      const visible = { opacity: 1, filter: "blur(0px)" };
      return track(`tldr-${i}`, [[0, hidden], [from, hidden, EASE.out], [from + 500, visible], ...jump(reset, visible, hidden)]);
    }),
    track("tldr-source", [
      [0, shown(false)],
      [at("tldr") + TLDR.length * TLDR_GAP, shown(false), EASE.out],
      [at("tldr") + TLDR.length * TLDR_GAP + 300, shown(true)],
      ...jump(reset, shown(true), shown(false)),
    ]),
    track("actions-label", [
      [0, shown(false)],
      [at("actions") - 200, shown(false), EASE.out],
      [at("actions") + 100, shown(true)],
      ...jump(reset, shown(true), shown(false)),
    ]),
    ...ACTION_ITEMS.map((_, i) => {
      const from = at("actions") + i * ACTION_GAP;
      const hidden = { opacity: 0, transform: "translateY(8px)" };
      const visible = { opacity: 1, transform: "translateY(0px)" };
      return track(`action-${i}`, [[0, hidden], [from, hidden, EASE.out], [from + 400, visible], ...jump(reset, visible, hidden)]);
    }),
    track("check-fill", [
      [0, { opacity: 0, transform: "scale(0.6)" }],
      [at("check") + 100, { opacity: 0, transform: "scale(0.6)" }, EASE.pop],
      [at("check") + 350, { opacity: 1, transform: "scale(1)" }],
      ...jump(reset, { opacity: 1, transform: "scale(1)" }, { opacity: 0, transform: "scale(0.6)" }),
    ]),
    track("check-mark", [
      [0, { clipPath: "inset(0 100% 0 0)" }],
      [at("check") + 180, { clipPath: "inset(0 100% 0 0)" }, EASE.out],
      [at("check") + 450, { clipPath: "inset(0 0% 0 0)" }],
      ...jump(reset, { clipPath: "inset(0 0% 0 0)" }, { clipPath: "inset(0 100% 0 0)" }),
    ]),
    track("check-text", [
      [0, shown(true)],
      [at("check") + 250, shown(true), EASE.out],
      [at("check") + 550, { opacity: 0.45 }],
      ...jump(reset, { opacity: 0.45 }, shown(true)),
    ]),
    track("check-strike", [
      [0, { transform: "scaleX(0)" }],
      [at("check") + 250, { transform: "scaleX(0)" }, EASE.inOut],
      [at("check") + 550, { transform: "scaleX(1)" }],
      ...jump(reset, { transform: "scaleX(1)" }, { transform: "scaleX(0)" }),
    ]),

    // Short loops on their own clock: the recording dot and the level bars.
    track("rec-dot", [[0, { opacity: 1, transform: "scale(1)" }, EASE.inOut], [600, { opacity: 0.35, transform: "scale(0.75)" }, EASE.inOut], [1200, { opacity: 1, transform: "scale(1)" }]], 1200),
    ...[520, 680, 600, 740, 560, 660, 700].map((duration, i) =>
      track(
        `rec-bar-${i}`,
        [
          [0, { transform: "scaleY(0.3)" }, EASE.inOut],
          [duration * 0.35, { transform: `scaleY(${[1, 0.8, 0.95, 0.7, 1, 0.85, 0.75][i]})` }, EASE.inOut],
          [duration * 0.7, { transform: "scaleY(0.5)" }, EASE.inOut],
          [duration, { transform: "scaleY(0.3)" }],
        ],
        duration,
      ),
    ),
  ];
  return tracks;
}
